import { useEffect, useState, type DependencyList } from "react";

import type { AppRole, Organisation } from "@/lib/types";

export type View =
  | "dashboard"
  | "setup"
  | "actions"
  | "inbox"
  | "requisitions"
  | "approvals"
  | "rfqs"
  | "orders"
  | "receipts"
  | "invoices"
  | "money"
  | "value"
  | "reports"
  | "exports"
  | "accounting"
  | "assurance"
  | "control-passports"
  | "internal-controls"
  | "industry-templates"
  | "controlled-pilots"
  | "vendors"
  | "contracts"
  | "reorders"
  | "budgets"
  | "network"
  | "inventory"
  | "assets"
  | "campaigns"
  | "plan"
  | "team"
  | "branding"
  | "content"
  | "trusted-network"
  | "audit";
export type Permission =
  | "read"
  | "requisition"
  | "approve"
  | "procure"
  | "receive"
  | "finance"
  | "manage";

export const permissionMap: Record<AppRole, (Permission | "*")[]> = {
  platform_owner: ["*"],
  tenant_admin: [
    "read",
    "requisition",
    "approve",
    "procure",
    "receive",
    "finance",
    "manage",
  ],
  requester: ["read", "requisition"],
  approver: ["read", "approve"],
  procurement: ["read", "procure"],
  receiver: ["read", "receive"],
  finance: ["read", "finance"],
  auditor: ["read"],
};

export type ViewProps = {
  organisation: Organisation;
  refresh: number;
  query: string;
  can: (permission: Permission) => boolean;
  changed: () => void;
};

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
};

export function useData<T>(loader: () => Promise<T>, dependencies: DependencyList) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    loader()
      .then((value) => {
        if (live) setData(value);
      })
      .catch((reason: Error) => {
        if (live) setError(reason.message);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // The caller supplies the complete dependency list for its loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  return { data, error, loading };
}

export function matches(query: string, ...values: unknown[]) {
  const normalized = query.trim().toLowerCase();
  return (
    !normalized ||
    values.some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(normalized),
    )
  );
}

export function reportActionError(reason: unknown) {
  window.alert(
    (reason as Error).message || "RAQOZ could not complete this action",
  );
}

export function localDateTimeInputValue() {
  const value = new Date();
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 16);
}
